<template>
  <div>
    <h1 v-html="heading" />

    <section>
      <article class="flow-group">
        <div class="header-with-icon push-down">
          <span class="icon-large" :class="'policy-type-name-' + [vueModel.Type]" />
          <ul class="no-bullet">
            <li
              v-for="(asset, index) in vueModel.Asset.AssetDetailLines"
              :key="index"
              v-html="asset"
            />
          </ul>
        </div>

        <div v-if="!registrationOrVinSetOnInit" class="control">
          <h-3-and-tooltip
            :h3="proposalModel.Label_CaravanInfo"
            :tooltip="proposalModel.Tooltip_CaravanInfo"
          />
          <div>
            <div v-if="vueModel.AdditionalInformation.IsRegistered" class="row">
              <div class="medium-8 columns">
                <Field
                  id="registrationNumber"
                  v-model="vueModel.AdditionalInformation.RegistrationNumber"
                  :rules="{
                    required: vueModel.AdditionalInformation.IsRegistered,
                    min: 2,
                    max: 10,
                    alpha_num: true
                  }"
                  type="text"
                  name="RegistrationNumber"
                  :placeholder="proposalModel.Placeholder_RegistrationNumber"
                  maxlength="10"
                  label="Nummerplade"
                />
                <ValidationError name="RegistrationNumber" />
              </div>

              <div class="medium-4 columns">
                <button
                  class="button"
                  :disabled="vueModel.AdditionalInformation.RegistrationNumber === ''"
                  @click="searchCaravan"
                >
                  Søg
                </button>
              </div>
            </div>
            <div v-if="!vueModel.AdditionalInformation.IsRegistered" class="row">
              <div class="medium-12 columns">
                <input
                  id="vinNumber"
                  v-model="vueModel.AdditionalInformation.VinNumber"
                  :ruls="{
                    required: !vueModel.AdditionalInformation.IsRegistered,
                    min: 1,
                    max: 20,
                    alpha_num: true
                  }"
                  type="text"
                  name="VinNumber"
                  :placeholder="proposalModel.Placeholder_VinNumber"
                  maxlength="20"
                  label="Stelnummer"
                />
                <ValidationError name="VinNumber" />
              </div>
            </div>
          </div>

          <div v-if="caravanFound" class="info-box is-shown info-box-align-left">
            <p>
              {{ vueModel.Asset.AssetDetailSingleLine }}<br />
              <span
                v-if="vueModel.AdditionalInformation.RegistrationNumber !== ''"
                v-html="registrationNumberText"
              /><br />
              <span v-if="vueModel.AdditionalInformation.VinNumber !== ''" v-html="vinNumberText" />
            </p>
          </div>
        </div>

        <div class="control">
          <h-3-and-tooltip
            :h3="proposalModel.Label_IsCaravanInsured"
            :tooltip="proposalModel.Tooltip_IsCaravanInsured"
          />

          <div class="row">
            <div class="small-6 columns">
              <div class="radio-stacked">
                <Field
                  id="hasInsuranceYes"
                  v-model="vueModel.AdditionalInformation.HasInsurance"
                  :rules="{ required: vueModel.AdditionalInformation.HasInsurance === null }"
                  type="radio"
                  name="HasInsurance"
                  :value="true"
                  :label="decodeHtml(proposalModel.FieldName_IsCaravanInsured)"
                  @change="resetAll"
                />
                <label
                  for="hasInsuranceYes"
                  v-html="proposalModel.TakeoverModel?.SharedProposalContent.LabelYes"
                />
              </div>
            </div>
            <div class="small-6 columns">
              <div class="radio-stacked">
                <input
                  id="hasInsuranceNo"
                  v-model="vueModel.AdditionalInformation.HasInsurance"
                  :rules="{ required: vueModel.AdditionalInformation.HasInsurance === null }"
                  type="radio"
                  name="HasInsurance"
                  :value="false"
                  :label="decodeHtml(proposalModel.FieldName_IsCaravanInsured)"
                  @change="resetAll"
                />
                <label
                  for="hasInsuranceNo"
                  v-html="proposalModel.TakeoverModel?.SharedProposalContent.LabelNo"
                />
              </div>
            </div>
            <div class="small-12 columns">
              <ValidationError name="HasInsurance" />
            </div>
          </div>
        </div>

        <div
          v-if="vueModel.AdditionalInformation.HasInsurance == false"
          id="SelectedTakeoverDate"
          class="control"
        >
          <h-3-and-tooltip
            :h3="proposalModel.Label_StartDate"
            :tooltip="proposalModel.Tooltip_StartDate"
          />
          <Field
            v-slot="{ field }"
            name="TakeoverDate"
            :model-value="vueModel.AdditionalInformation.StartDate"
            :rules="{ required: true }"
            :label="decodeHtml(proposalModel.FieldName_StartDate)"
          >
            <date-picker
              id="TakeoverDate"
              :name="field.name"
              :min-date="currentDate"
              :model-value="vueModel.AdditionalInformation.StartDate"
              @update:model-value="dateChanged"
              class="datepicker"
              :placeholder="proposalModel.PlaceholderSelectStartDate"
            />
          </Field>
          <ValidationError name="TakeoverDate" />
        </div>

        <takeover-information
          v-if="vueModel.AdditionalInformation.HasInsurance"
          :external-vue-model="vueModel.AdditionalInformation.TakeoverInformation"
          :external-takeover-model="proposalModel.TakeoverModel"
          :mitid-model="mitIdModel"
        />
      </article>
    </section>

    <v-modal :is-open="showErrorModal" data-test="checkout-caravan" @close="showErrorModal = false">
      <template #title>
        <h2 v-html="proposalModel.ErrorModalGenericTitle" />
      </template>
      <template #content>
        <div v-html="proposalModel.ErrorModalGenericDescription" />
      </template>
    </v-modal>

    <v-modal
      :is-open="showNotFoundModal"
      data-test="checkout-caravan"
      @close="showNotFoundModal = false"
    >
      <template #title>
        <h2 v-html="proposalModel.ErrorModalNotFoundTitle" />
      </template>
      <template #content>
        <div v-html="proposalModel.ErrorModalNotFoundDescription" />
      </template>
    </v-modal>
  </div>
</template>

<script>
import axios from 'axios';
import Utility from '@/common/UtilityFunctions.js';
import TakeoverInformation from './Shared/AdditionalInformation/TakeoverInformation.vue';
import H3AndTooltip from '@/components/UI/H3AndTooltip.vue';
import ValidationError from '@/components/UI/ValidationError.vue';
import mixin from './CheckoutMixin.vue';
import DatePicker from '@/components/UI/Pickadate.vue';

export default {
  name: 'Caravan',
  components: {
    TakeoverInformation,
    H3AndTooltip,
    ValidationError,
    DatePicker
  },
  mixins: [mixin],
  props: {
    externalVueModel: {
      type: Object,
      default() {
        return {};
      }
    },
    proposalModel: {
      type: Object,
      default() {
        return {};
      }
    },
    extVueModel: {
      type: Object,
      default() {
        return {};
      }
    }
  },
  data() {
    return {
      config: {
        headers: {
          'Content-Type': 'application/json'
        }
      },
      caravanFound: false,
      showErrorModal: false,
      showNotFoundModal: false,
      caravanSearchModel: {
        registrationNumber: null,
        vinNumber: null
      },
      registrationOrVinSetOnInit: false,
      licensePlate: true,
      vueModel: this.externalVueModel,
      mitIdModel: this.extVueModel.Content.MitIdModal
    };
  },
  computed: {
    heading() {
      if (
        this.vueModel &&
        this.vueModel.Name &&
        this.proposalModel &&
        this.proposalModel.LabelExtraInformationForYour
      ) {
        return `${
          this.proposalModel.LabelExtraInformationForYour
        } ${this.vueModel.Name.toLowerCase()}`;
      }

      return '';
    },
    vinNumberText() {
      if (
        this.caravanSearchModel &&
        this.caravanSearchModel.vinNumber &&
        this.proposalModel &&
        this.proposalModel.Label_InfoBox_VinNumber
      ) {
        return `${this.proposalModel.Label_InfoBox_VinNumber}: ${this.caravanSearchModel.vinNumber}`;
      }

      return '';
    },
    registrationNumberText() {
      if (
        this.caravanSearchModel &&
        this.caravanSearchModel.registrationNumber &&
        this.proposalModel &&
        this.proposalModel.Label_InfoBox_RegistrationNumber
      ) {
        return `${this.proposalModel.Label_InfoBox_RegistrationNumber}: ${this.caravanSearchModel.registrationNumber}`;
      }

      return '';
    }
  },
  watch: {
    'vueModel.AdditionalInformation.HasInsurance': function () {
      if (this.vueModel.AdditionalInformation.HasInsurance) {
        this.vueModel.AdditionalInformation.StartDate = null;
      }

      if (this.vueModel.AdditionalInformation.HasInsurance) {
        this.$nextTick(() => {
          window.Utility.scrollToElem(
            $('#CurrentInsuranceCompany'),
            window.CONSTANT.SCROLLING.flowPageDefaultOffset
          );
        });
      }
    },
    'vueModel.AdditionalInformation.TakeoverInformation.SelectedInsuranceCompany': function () {
      if (
        this.vueModel.AdditionalInformation.TakeoverInformation.SelectedInsuranceCompany !== null
      ) {
        this.$nextTick(() => {
          window.Utility.scrollToElem(
            $('#TakeoverType'),
            window.CONSTANT.SCROLLING.flowPageDefaultOffset
          );
        });
      }
    },
    'vueModel.AdditionalInformation.TakeoverInformation.TakeoverType': function () {
      if (this.vueModel.AdditionalInformation.TakeoverInformation.TakeoverType === 0) {
        this.$nextTick(() => {
          window.Utility.scrollToElem(
            $('#SelectedTakeoverDate'),
            window.CONSTANT.SCROLLING.flowPageDefaultOffset
          );
        });
      }
    },
    'vueModel.AdditionalInformation.RegistrationNumber': function () {
      this.$parent.$emit(
        'disable-next-button',
        this.vueModel.AdditionalInformation.RegistrationNumber !=
          this.caravanSearchModel.registrationNumber
      );
    },
    'vueModel.AdditionalInformation.VinNumber': function () {
      if (this.vueModel.AdditionalInformation.IsRegistered) {
        this.$parent.$emit(
          'disable-next-button',
          this.vueModel.AdditionalInformation.VinNumber != this.caravanSearchModel.vinNumber
        );
      } else {
        var hasVinNumber = this.vueModel.AdditionalInformation.VinNumber !== '';
        this.$parent.$emit('disable-next-button', !hasVinNumber);
        this.caravanFound = hasVinNumber;
        this.caravanSearchModel.vinNumber = this.vueModel.AdditionalInformation.VinNumber;
      }
    },
    vueModel: {
      handler() {
        this.$emit('vue-model-updated', this.vueModel);
      },
      deep: true
    }
  },
  mounted() {
    var hasVinNumber = this.vueModel.AdditionalInformation.VinNumber !== '';
    this.registrationOrVinSetOnInit = this.vueModel.AdditionalInformation.HasIdentifier;
    this.$parent.$emit('disable-next-button', !this.registrationOrVinSetOnInit && !hasVinNumber);
  },
  methods: {
    decodeHtml: Utility.decodeHtml,
    dateChanged(param) {
      this.vueModel.AdditionalInformation.StartDate = param;
    },
    searchCaravan() {
      const data = {
        externalId: this.vueModel.ExternalProposalNumber,
        vinNumber: this.vueModel.AdditionalInformation.IsRegistered
          ? null
          : this.vueModel.AdditionalInformation.VinNumber,
        registrationNumber: this.vueModel.AdditionalInformation.IsRegistered
          ? this.vueModel.AdditionalInformation.RegistrationNumber
          : null
      };
      axios
        .post(Utility.constants().API.proposal_caravan_search, JSON.stringify(data), this.config)
        .then((response) => {
          this.caravanFound = true;
          this.$parent.$emit('disable-next-button', false);
          const resp = response.data;

          this.caravanSearchModel.registrationNumber = resp.RegistrationNumber;
          this.caravanSearchModel.vinNumber = resp.VinNumber;

          this.vueModel.AdditionalInformation.HasIdentifier = true;
          this.vueModel.AdditionalInformation.RegistrationNumber = resp.RegistrationNumber;
          this.vueModel.AdditionalInformation.VinNumber = resp.VinNumber;

          var assetDetails = resp.Make + ' ' + resp.Model + ' ' + resp.Variant + ' ' + resp.Year;
          this.vueModel.Asset.AssetDetailSingleLine = assetDetails;
          this.vueModel.Asset.AssetDetailLines = [assetDetails];
        })
        .catch((err) => {
          this.caravanFound = false;
          this.$parent.$emit('disable-next-button', true);
          if (err.response.status === 404) {
            this.showNotFoundModal = true;
          } else {
            this.showErrorModal = true;
          }
        });
    }
  }
};
</script>
